import { type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { SeoHead } from '@/components/seo/SeoHead';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import {
  Shield, Lock, Eye, Users, Database, Clock, Mail,
  Phone, ArrowLeft, CheckCircle, AlertCircle, Github, Linkedin,
} from 'lucide-react';

const P = {
  bg: '#0B0B0B',
  card: '#111111',
  orange: '#FF4500',
  cream: '#F2EDE3',
  muted: '#8C8377',
  border: 'rgba(255,255,255,0.06)',
  borderOrange: 'rgba(255,69,0,0.2)',
};

const Section = ({ num, title, children }: { num: number | string; title: string; children: ReactNode }) => (
  <section>
    <h2 className="text-2xl font-bold mb-4 flex items-center gap-3" style={{ color: P.cream, fontFamily: "'JetBrains Mono', monospace" }}>
      <span className="flex items-center justify-center w-8 h-8 rounded text-sm font-bold flex-shrink-0" style={{ background: 'rgba(255,69,0,0.12)', color: P.orange, border: `1px solid ${P.borderOrange}` }}>
        {num}
      </span>
      {title}
    </h2>
    {children}
  </section>
);

const Card = ({ children, orange = false, className = '' }: { children: ReactNode; orange?: boolean; className?: string }) => (
  <div className={`rounded-xl p-6 ${className}`} style={{ background: orange ? 'rgba(255,69,0,0.05)' : P.card, border: `1px solid ${orange ? P.borderOrange : P.border}` }}>
    {children}
  </div>
);

const PoliticaPrivacidade = () => {
  const lastUpdated = '04 de Fevereiro de 2026';

  return (
    <>
      <SeoHead
        route="/politica-privacidade"
        lang="pt-BR"
        title="Política de Privacidade - Johnny Vaz"
        description="Política de Privacidade do site johnnyvaz.com.br em conformidade com a LGPD"
      />
      <Navbar />

      <div style={{ background: P.bg, minHeight: '100vh' }}>
        <main className="container mx-auto px-4 py-12 md:py-20 max-w-5xl">
          <div className="mb-8">
            <Link to="/">
              <Button variant="ghost" size="sm" className="gap-2" style={{ color: P.muted }}>
                <ArrowLeft className="h-4 w-4" />
                Voltar ao início
              </Button>
            </Link>
          </div>

          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center p-3 rounded-xl mb-4" style={{ background: 'rgba(255,69,0,0.1)', border: `1px solid ${P.borderOrange}` }}>
              <Shield className="h-8 w-8" style={{ color: P.orange }} />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: P.cream, fontFamily: "'JetBrains Mono', monospace" }}>
              Política de Privacidade
            </h1>
            <p className="max-w-2xl mx-auto" style={{ color: P.muted }}>
              Sua privacidade é importante para mim. Esta política explica como coleto,
              uso e protejo seus dados pessoais em conformidade com a LGPD.
            </p>
            <p className="text-sm mt-4" style={{ color: P.muted }}>Última atualização: {lastUpdated}</p>
          </div>

          {/* Resumo Rápido */}
          <Card orange className="mb-12">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: P.cream }}>
              <Eye className="h-5 w-5" style={{ color: P.orange }} />
              Resumo Rápido
            </h2>
            <div className="grid md:grid-cols-2 gap-4 text-sm">
              {[
                'Coleto apenas dados necessários para os serviços',
                'Seus dados nunca são vendidos a terceiros',
                'Você pode solicitar acesso, correção ou exclusão dos seus dados',
                'Scripts de terceiros só carregam com seu consentimento explícito',
              ].map((item) => (
                <div key={item} className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0 text-green-400" />
                  <span style={{ color: P.cream }}>{item}</span>
                </div>
              ))}
            </div>
          </Card>

          <div className="space-y-12">
            <Section num={1} title="Introdução">
              <Card>
                <p style={{ color: P.muted }} className="leading-relaxed">
                  <strong style={{ color: P.cream }}>Johnny Vaz</strong>, desenvolvedor full-stack, está comprometido em proteger sua privacidade
                  e seus dados pessoais. Esta Política de Privacidade descreve como coleto, uso, armazeno
                  e protejo suas informações quando você utiliza meu site e serviços.
                </p>
                <p style={{ color: P.muted }} className="leading-relaxed mt-4">
                  Esta política está em conformidade com a <strong style={{ color: P.cream }}>Lei Geral de Proteção de Dados (LGPD)</strong> -
                  Lei nº 13.709/2018 - e outras regulamentações aplicáveis no Brasil.
                </p>
              </Card>
            </Section>

            <Section num={2} title="Dados que Coleto">
              <div className="space-y-4">
                <Card>
                  <h3 className="font-semibold mb-3 flex items-center gap-2" style={{ color: P.cream }}>
                    <Users className="h-5 w-5" style={{ color: P.orange }} />
                    Dados fornecidos por você
                  </h3>
                  <ul className="space-y-2">
                    {[
                      { label: 'Dados de contato:', desc: 'nome, e-mail, telefone/WhatsApp' },
                      { label: 'Dados profissionais:', desc: 'empresa, cargo, projeto de interesse' },
                      { label: 'Comunicações:', desc: 'mensagens enviadas pelo formulário de contato' },
                    ].map(({ label, desc }) => (
                      <li key={label} className="flex items-start gap-2">
                        <span style={{ color: P.orange }}>•</span>
                        <span style={{ color: P.muted }}><strong style={{ color: P.cream }}>{label}</strong> {desc}</span>
                      </li>
                    ))}
                  </ul>
                </Card>
                <Card>
                  <h3 className="font-semibold mb-3 flex items-center gap-2" style={{ color: P.cream }}>
                    <Database className="h-5 w-5" style={{ color: P.orange }} />
                    Dados coletados automaticamente (com seu consentimento)
                  </h3>
                  <ul className="space-y-2">
                    {[
                      { label: 'Dados de navegação:', desc: 'páginas visitadas, tempo de permanência' },
                      { label: 'Dados técnicos:', desc: 'endereço IP, tipo de navegador, dispositivo' },
                      { label: 'Cookies:', desc: 'identificadores para melhorar sua experiência' },
                    ].map(({ label, desc }) => (
                      <li key={label} className="flex items-start gap-2">
                        <span style={{ color: P.orange }}>•</span>
                        <span style={{ color: P.muted }}><strong style={{ color: P.cream }}>{label}</strong> {desc}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-4 p-3 rounded-lg" style={{ background: 'rgba(74,222,128,0.08)', border: '1px solid rgba(74,222,128,0.2)' }}>
                    <p className="text-sm text-green-400 flex items-start gap-2">
                      <Shield className="h-4 w-4 mt-0.5 flex-shrink-0" />
                      <span><strong>Importante:</strong> Scripts de análise só são carregados após seu consentimento explícito através do banner de cookies.</span>
                    </p>
                  </div>
                </Card>
              </div>
            </Section>

            <Section num={3} title="Como Uso seus Dados">
              <Card>
                <ul className="space-y-3">
                  {[
                    'Responder suas dúvidas e solicitações de contato',
                    'Elaborar propostas de projetos e serviços',
                    'Enviar comunicações sobre novos projetos (apenas com consentimento)',
                    'Analisar o uso do site para melhorar a experiência',
                    'Cumprir obrigações legais e regulatórias',
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-green-400 mt-0.5 flex-shrink-0" />
                      <span style={{ color: P.muted }}>{item}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            </Section>

            <Section num={4} title="Base Legal para Tratamento">
              <p className="mb-4" style={{ color: P.muted }}>
                Trato seus dados pessoais com base nas seguintes hipóteses previstas na LGPD (Art. 7º):
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                {[
                  { title: 'Consentimento', desc: 'Para envio de comunicações e uso de cookies analíticos.' },
                  { title: 'Execução de Contrato', desc: 'Para prestar os serviços contratados por você.' },
                  { title: 'Interesse Legítimo', desc: 'Para melhorar os serviços e garantir a segurança do site.' },
                  { title: 'Obrigação Legal', desc: 'Para cumprir exigências legais e regulatórias.' },
                ].map(({ title, desc }) => (
                  <Card key={title} className="p-4">
                    <h4 className="font-semibold text-sm mb-2" style={{ color: P.cream }}>{title}</h4>
                    <p className="text-sm" style={{ color: P.muted }}>{desc}</p>
                  </Card>
                ))}
              </div>
            </Section>

            <Section num={5} title="Política de Cookies">
              <Card>
                <p className="mb-4" style={{ color: P.muted }}>Utilizo cookies e tecnologias similares para melhorar sua experiência:</p>
                <div className="space-y-4">
                  <div className="p-4 rounded-lg" style={{ border: `1px solid ${P.border}` }}>
                    <div className="flex items-center gap-2 mb-2">
                      <Lock className="h-4 w-4 text-green-400" />
                      <h4 className="font-semibold" style={{ color: P.cream }}>Cookies Necessários</h4>
                      <span className="text-xs px-2 py-0.5 rounded" style={{ background: 'rgba(74,222,128,0.15)', color: '#4ade80' }}>Sempre ativos</span>
                    </div>
                    <p className="text-sm" style={{ color: P.muted }}>Essenciais para o funcionamento do site. Não podem ser desabilitados.</p>
                  </div>
                  <div className="p-4 rounded-lg" style={{ border: `1px solid ${P.border}` }}>
                    <div className="flex items-center gap-2 mb-2">
                      <Eye className="h-4 w-4" style={{ color: P.orange }} />
                      <h4 className="font-semibold" style={{ color: P.cream }}>Cookies Analíticos</h4>
                      <span className="text-xs px-2 py-0.5 rounded" style={{ background: 'rgba(255,69,0,0.15)', color: P.orange }}>Requer consentimento</span>
                    </div>
                    <p className="text-sm" style={{ color: P.muted }}>Ajudam a entender como você usa o site (ex: Google Analytics).</p>
                  </div>
                  <div className="p-4 rounded-lg" style={{ border: `1px solid ${P.border}` }}>
                    <div className="flex items-center gap-2 mb-2">
                      <Users className="h-4 w-4" style={{ color: P.muted }} />
                      <h4 className="font-semibold" style={{ color: P.cream }}>Cookies de Personalização</h4>
                      <span className="text-xs px-2 py-0.5 rounded" style={{ background: 'rgba(140,131,119,0.2)', color: P.muted }}>Requer consentimento</span>
                    </div>
                    <p className="text-sm" style={{ color: P.muted }}>Lembram suas preferências como tema escuro/claro.</p>
                  </div>
                </div>
                <div className="mt-4 p-4 rounded-lg" style={{ background: 'rgba(255,69,0,0.08)', border: `1px solid ${P.borderOrange}` }}>
                  <p className="text-sm" style={{ color: P.orange }}>
                    <strong>Como gerenciar:</strong> Altere suas preferências a qualquer momento clicando em "Configurar Cookies" no banner.
                  </p>
                </div>
              </Card>
            </Section>

            <Section num={6} title="Compartilhamento de Dados">
              <Card>
                <p className="mb-4" style={{ color: P.muted }}>
                  <strong style={{ color: P.cream }}>Não vendo seus dados pessoais.</strong> Posso compartilhar informações apenas:
                </p>
                <ul className="space-y-3">
                  {[
                    { label: 'Prestadores de serviço:', desc: 'hospedagem (Vercel, Netlify), e-mail (Resend), sempre sob contratos de confidencialidade.' },
                    { label: 'Obrigações legais:', desc: 'quando exigido por lei ou autoridade competente.' },
                    { label: 'Com seu consentimento:', desc: 'em qualquer outra situação, apenas com autorização prévia.' },
                  ].map(({ label, desc }) => (
                    <li key={label} className="flex items-start gap-3">
                      <span style={{ color: P.orange }} className="font-bold">•</span>
                      <span style={{ color: P.muted }}><strong style={{ color: P.cream }}>{label}</strong> {desc}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            </Section>

            <Section num={7} title="Retenção de Dados">
              <Card>
                <div className="flex items-start gap-3 mb-4">
                  <Clock className="h-5 w-5 mt-1" style={{ color: P.orange }} />
                  <p style={{ color: P.muted }}>Mantenho seus dados apenas pelo tempo necessário:</p>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  {[
                    { label: 'Dados de contato', value: 'Até 2 anos após último contato' },
                    { label: 'Dados de projetos', value: '5 anos (obrigação legal)' },
                    { label: 'Cookies analíticos', value: 'Até 2 anos' },
                    { label: 'Consentimento', value: '1 ano ou até revogação' },
                  ].map(({ label, value }) => (
                    <div key={label} className="p-3 rounded-lg" style={{ background: 'rgba(255,255,255,0.04)' }}>
                      <p className="font-semibold text-sm" style={{ color: P.cream }}>{label}</p>
                      <p className="text-sm" style={{ color: P.muted }}>{value}</p>
                    </div>
                  ))}
                </div>
              </Card>
            </Section>

            <Section num={8} title="Seus Direitos (LGPD Art. 18)">
              <Card orange>
                <p className="mb-4" style={{ color: P.muted }}>
                  Você tem os seguintes direitos em relação aos seus dados pessoais:
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  {[
                    { title: 'Confirmação e Acesso', desc: 'Saber se trato seus dados e acessá-los' },
                    { title: 'Correção', desc: 'Corrigir dados incompletos ou incorretos' },
                    { title: 'Eliminação', desc: 'Solicitar exclusão dos dados desnecessários' },
                    { title: 'Portabilidade', desc: 'Receber seus dados em formato estruturado' },
                    { title: 'Revogação', desc: 'Retirar consentimento a qualquer momento' },
                    { title: 'Oposição', desc: 'Opor-se ao tratamento em certas situações' },
                  ].map(({ title, desc }) => (
                    <div key={title} className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 mt-1 flex-shrink-0" style={{ color: P.orange }} />
                      <div>
                        <p className="font-semibold text-sm" style={{ color: P.cream }}>{title}</p>
                        <p className="text-xs" style={{ color: P.muted }}>{desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 p-3 rounded-lg" style={{ background: 'rgba(255,69,0,0.08)' }}>
                  <p className="text-sm" style={{ color: P.orange }}>
                    Para exercer seus direitos, entre em contato pelos canais abaixo. Respondo em até 15 dias úteis.
                  </p>
                </div>
              </Card>
            </Section>

            <Section num={9} title="Segurança dos Dados">
              <Card>
                <p className="mb-4" style={{ color: P.muted }}>Implemento medidas técnicas e organizacionais para proteger seus dados:</p>
                <div className="grid md:grid-cols-3 gap-4">
                  {[
                    { icon: <Lock className="h-8 w-8 text-green-400 mx-auto mb-2" />, title: 'Criptografia SSL/TLS', desc: 'Dados em trânsito protegidos' },
                    { icon: <Shield className="h-8 w-8 mx-auto mb-2" style={{ color: P.orange }} />, title: 'Acesso Restrito', desc: 'Apenas eu acesso os dados' },
                    { icon: <Database className="h-8 w-8 mx-auto mb-2" style={{ color: P.muted }} />, title: 'Backup Seguro', desc: 'Cópias de segurança regulares' },
                  ].map(({ icon, title, desc }) => (
                    <div key={title} className="text-center p-4 rounded-lg" style={{ border: `1px solid ${P.border}` }}>
                      {icon}
                      <p className="font-semibold text-sm" style={{ color: P.cream }}>{title}</p>
                      <p className="text-xs" style={{ color: P.muted }}>{desc}</p>
                    </div>
                  ))}
                </div>
              </Card>
            </Section>

            <Section num={10} title="Contato">
              <Card orange>
                <p className="mb-6" style={{ color: P.muted }}>
                  Para dúvidas sobre esta política ou para exercer seus direitos, entre em contato:
                </p>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Users className="h-5 w-5" style={{ color: P.orange }} />
                    <span className="font-semibold" style={{ color: P.cream }}>Johnny Vaz</span>
                    <span className="text-sm" style={{ color: P.muted }}>- Full-Stack Developer</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Mail className="h-5 w-5" style={{ color: P.orange }} />
                    <a href="mailto:contato@johnnyvaz.com.br" style={{ color: P.orange }} className="hover:underline">contato@johnnyvaz.com.br</a>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="h-5 w-5" style={{ color: P.orange }} />
                    <a href="tel:+5516997787674" style={{ color: P.orange }} className="hover:underline">+55 16 99778-7674</a>
                  </div>
                  <div className="flex items-center gap-3">
                    <Github className="h-5 w-5" style={{ color: P.orange }} />
                    <a href="https://github.com/johnnyvaz" target="_blank" rel="noopener noreferrer" style={{ color: P.orange }} className="hover:underline">github.com/johnnyvaz</a>
                  </div>
                  <div className="flex items-center gap-3">
                    <Linkedin className="h-5 w-5" style={{ color: P.orange }} />
                    <a href="https://linkedin.com/in/johnnyvaz" target="_blank" rel="noopener noreferrer" style={{ color: P.orange }} className="hover:underline">linkedin.com/in/johnnyvaz</a>
                  </div>
                </div>
                <div className="mt-6 p-4 rounded-lg" style={{ background: 'rgba(255,255,255,0.04)' }}>
                  <p className="text-sm flex items-start gap-2" style={{ color: P.muted }}>
                    <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: P.orange }} />
                    <span>
                      Você também pode registrar uma reclamação junto à{' '}
                      <strong style={{ color: P.cream }}>Autoridade Nacional de Proteção de Dados (ANPD)</strong>{' '}
                      caso entenda que seus direitos não foram atendidos.
                    </span>
                  </p>
                </div>
              </Card>
            </Section>

            <Section num={11} title="Alterações nesta Política">
              <Card>
                <p style={{ color: P.muted }}>
                  Esta política pode ser atualizada periodicamente. Quando fizer alterações significativas,
                  notificarei através do site. Recomendo revisar esta página regularmente.
                </p>
                <p className="text-sm mt-4" style={{ color: P.muted }}>
                  <strong style={{ color: P.cream }}>Versão:</strong> 1.0 |{' '}
                  <strong style={{ color: P.cream }}>Última atualização:</strong> {lastUpdated}
                </p>
              </Card>
            </Section>
          </div>

          <div className="text-center mt-12">
            <Link to="/">
              <Button variant="outline" className="gap-2" style={{ borderColor: P.borderOrange, color: P.orange }}>
                <ArrowLeft className="h-4 w-4" />
                Voltar ao início
              </Button>
            </Link>
          </div>
        </main>
      </div>

      <Footer />
    </>
  );
};

export default PoliticaPrivacidade;
